import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Brain, Play, Check, X, Trophy, Clock, Eye } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const MentalMemoryGame = ({ user, examMode = false, examConfig = null, onExamComplete = null, onExit = null }) => {
  const [gameState, setGameState] = useState('config'); // config, ready, flashing, input, result
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [config, setConfig] = useState(
    examMode && examConfig ? {
      sequenceLength: examConfig.sequenceLength?.toString() || '4',
      flashSpeed: (examConfig.flashSpeed / 1000)?.toString() || '0.8',
      numberOfRounds: examConfig.numberOfQuestions?.toString() || '5'
    } : {
      sequenceLength: '4',
      flashSpeed: '0.8',
      numberOfRounds: '5'
    }
  );
  const [currentRound, setCurrentRound] = useState(0);
  const [sequence, setSequence] = useState([]);
  const [currentDigitIndex, setCurrentDigitIndex] = useState(0);
  const [displayDigit, setDisplayDigit] = useState('');
  const [userInput, setUserInput] = useState('');
  const [results, setResults] = useState([]);
  const [startTime, setStartTime] = useState(null);
  const [isFlashing, setIsFlashing] = useState(false);
  const autoStarted = useRef(false);
  const gameContainerRef = useRef(null);

  const enterFullScreen = async () => {
    try {
      if (gameContainerRef.current) {
        if (gameContainerRef.current.requestFullscreen) {
          await gameContainerRef.current.requestFullscreen();
        } else if (gameContainerRef.current.webkitRequestFullscreen) {
          await gameContainerRef.current.webkitRequestFullscreen();
        } else if (gameContainerRef.current.mozRequestFullScreen) {
          await gameContainerRef.current.mozRequestFullScreen();
        } else if (gameContainerRef.current.msRequestFullscreen) {
          await gameContainerRef.current.msRequestFullscreen();
        }
        setIsFullScreen(true);
      }
    } catch (error) {
      console.error('Error entering fullscreen:', error);
    }
  };

  const exitFullScreen = async () => {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        await document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen();
      }
      setIsFullScreen(false);
    } catch (error) {
      console.error('Error exiting fullscreen:', error);
    }
  };

  const handleExit = async () => {
    if (window.confirm('Are you sure you want to exit the game?')) {
      await exitFullScreen();
      if (onExit) {
        onExit();
      } else {
        setGameState('config');
        setResults([]);
        setCurrentRound(0);
      }
    }
  };

  // Auto-enter fullscreen when component mounts (not in exam mode)
  useEffect(() => {
    if (!examMode && !autoStarted.current) {
      autoStarted.current = true;
      // Enter fullscreen automatically when game is opened
      setTimeout(() => {
        enterFullScreen();
      }, 300);
    }
  }, []);

  // Auto-start game IMMEDIATELY when in exam mode
  useEffect(() => {
    if (examMode && examConfig?.autoStart && !autoStarted.current) {
      console.log('🎮 EXAM MODE: Starting Mental Memory Game immediately!');
      autoStarted.current = true;
      setGameState('flashing');
      setCurrentRound(0);
      setResults([]);
      setStartTime(Date.now());
      setTimeout(() => {
        startRound();
      }, 100);
    }
  }, [examMode, examConfig?.autoStart]);

  const generateSequence = (length) => {
    return Array.from({ length }, () => Math.floor(Math.random() * 10));
  };

  const proceedToReady = async () => {
    await enterFullScreen();
    setGameState('ready');
  };

  const startGame = () => {
    setGameState('flashing');
    setCurrentRound(0);
    setResults([]);
    setStartTime(Date.now());
    startRound();
  };

  const startRound = () => {
    const newSequence = generateSequence(parseInt(config.sequenceLength));
    setSequence(newSequence);
    setCurrentDigitIndex(0);
    setUserInput('');
    flashSequence(newSequence);
  };

  const flashSequence = (seq) => {
    setIsFlashing(true);
    let index = 0;

    const flashNext = () => {
      if (index < seq.length) {
        setDisplayDigit(seq[index].toString());
        setCurrentDigitIndex(index);

        setTimeout(() => {
          setDisplayDigit('');
          setTimeout(() => {
            index++;
            flashNext();
          }, 200); // Gap between digits
        }, parseFloat(config.flashSpeed) * 1000);
      } else {
        setIsFlashing(false);
        setGameState('input');
      }
    };

    flashNext();
  };

  const handleSubmit = () => {
    const correct = userInput === sequence.join('');
    const newResult = {
      round: currentRound + 1,
      sequence: sequence.join(''),
      userAnswer: userInput,
      correct
    };
    
    const updatedResults = [...results, newResult];
    setResults(updatedResults);

    if (currentRound + 1 < parseInt(config.numberOfRounds)) {
      // Next round
      setCurrentRound(currentRound + 1);
      setGameState('flashing');
      setTimeout(() => {
        startRound();
      }, 1000);
    } else {
      // Game over
      const correctCount = updatedResults.filter(r => r.correct).length;
      const accuracy = (correctCount / updatedResults.length) * 100;
      const timeTaken = Math.floor((Date.now() - startTime) / 1000);
      
      submitScore(correctCount, accuracy, timeTaken);
      setGameState('result');
    }
  };

  const submitScore = async (correctAnswers, accuracy, timeTaken) => {
    try {
      const token = localStorage.getItem('token');
      const scoreData = {
        game_name: 'mental_memory',
        score: correctAnswers,
        accuracy: parseFloat(accuracy.toFixed(2)),
        time_taken: timeTaken,
        game_config: {
          sequence_length: parseInt(config.sequenceLength),
          flash_speed: parseFloat(config.flashSpeed),
          total_rounds: parseInt(config.numberOfRounds)
        }
      };

      await axios.post(`${API}/api/game-scores`, scoreData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Score saved successfully!');
      
      if (examMode && onExamComplete) {
        onExamComplete({
          score: correctAnswers,
          maxScore: parseInt(config.numberOfRounds),
          accuracy,
          timeTaken
        });
      }
    } catch (error) {
      console.error('Error saving score:', error);
      toast.error('Failed to save score');
    }
  };

  const resetGame = () => {
    setGameState('config');
    setCurrentRound(0);
    setSequence([]);
    setUserInput('');
    setResults([]);
    setStartTime(null);
  };

  const renderGameContent = () => {
    // Config Screen
    if (gameState === 'config' && !examMode) {
      return (
        <div className="fixed inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 flex items-center justify-center p-4 overflow-auto">
        <div className="w-full max-w-3xl relative">
          <div className="text-center mb-6 animate-fade-in">
            <div className="w-20 h-20 bg-white/20 backdrop-blur-lg rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-2xl border-2 border-white/30">
              <Brain className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-4xl font-bold text-white mb-2 drop-shadow-lg">Mental Memory Game</h2>
            <p className="text-lg text-white/90">Remember the number sequence and type it back!</p>
          </div>

          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 shadow-2xl border border-white/20 space-y-4">
            <div>
              <Label className="text-white font-semibold mb-2 block">Sequence Length</Label>
              <Select value={config.sequenceLength} onValueChange={(v) => setConfig({...config, sequenceLength: v})}>
                <SelectTrigger className="bg-white/20 border-white/30 text-white h-12">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 Digits - Beginner</SelectItem>
                  <SelectItem value="4">4 Digits - Easy</SelectItem>
                  <SelectItem value="5">5 Digits - Medium</SelectItem>
                  <SelectItem value="6">6 Digits - Hard</SelectItem>
                  <SelectItem value="7">7 Digits - Expert</SelectItem>
                  <SelectItem value="8">8 Digits - Master</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-white font-semibold mb-2 block">Flash Speed</Label>
              <Select value={config.flashSpeed} onValueChange={(v) => setConfig({...config, flashSpeed: v})}>
                <SelectTrigger className="bg-white/20 border-white/30 text-white h-12">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0.5">⚡ Fast (0.5s)</SelectItem>
                  <SelectItem value="0.8">🎯 Medium (0.8s)</SelectItem>
                  <SelectItem value="1">🐢 Slow (1s)</SelectItem>
                  <SelectItem value="1.5">🐌 Very Slow (1.5s)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-white font-semibold mb-2 block">Number of Rounds</Label>
              <Select value={config.numberOfRounds} onValueChange={(v) => setConfig({...config, numberOfRounds: v})}>
                <SelectTrigger className="bg-white/20 border-white/30 text-white h-12">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 Rounds - Quick</SelectItem>
                  <SelectItem value="5">5 Rounds - Standard</SelectItem>
                  <SelectItem value="10">10 Rounds - Long</SelectItem>
                  <SelectItem value="15">15 Rounds - Marathon</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button 
              onClick={proceedToReady} 
              size="lg"
              className="w-full bg-white text-purple-600 hover:bg-white/90 text-lg py-4 font-bold shadow-xl hover:scale-105 transition-transform"
            >
              Continue →
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Ready Screen
  if (gameState === 'ready' && !examMode) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 flex items-center justify-center p-4">
        {!examMode && (
          <button
            onClick={handleExit}
            className="absolute top-4 left-4 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg backdrop-blur-lg border border-white/30 transition-all z-50"
          >
            ← Exit
          </button>
        )}
        <div className="w-full max-w-4xl text-center space-y-6 animate-fade-in">
          <div className="w-24 h-24 bg-white/20 backdrop-blur-lg rounded-full flex items-center justify-center mx-auto shadow-2xl border-2 border-white/30">
            <Brain className="w-12 h-12 text-white" />
          </div>
          
          <h2 className="text-4xl font-bold text-white drop-shadow-lg">Get Ready!</h2>
          
          <div className="bg-white/10 backdrop-blur-lg p-6 rounded-2xl shadow-2xl border border-white/20 space-y-4 max-w-2xl mx-auto">
            <p className="text-2xl text-white font-bold mb-4">
              How to Play:
            </p>
            <ol className="text-left text-white/90 space-y-3 text-base">
              <li className="flex items-start">
                <span className="bg-white/20 rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0 font-bold text-sm">1</span>
                <span>A sequence of {config.sequenceLength} digits will flash one by one</span>
              </li>
              <li className="flex items-start">
                <span className="bg-white/20 rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0 font-bold text-sm">2</span>
                <span>Watch carefully and remember the sequence</span>
              </li>
              <li className="flex items-start">
                <span className="bg-white/20 rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0 font-bold text-sm">3</span>
                <span>Type the complete sequence after all digits are shown</span>
              </li>
              <li className="flex items-start">
                <span className="bg-white/20 rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0 font-bold text-sm">4</span>
                <span>Complete {config.numberOfRounds} rounds to finish the game</span>
              </li>
            </ol>
          </div>

          <Button 
            onClick={startGame}
            size="lg"
            className="bg-white text-purple-600 hover:bg-white/90 text-xl py-5 px-12 font-bold shadow-2xl hover:scale-105 transition-transform"
          >
            <Play className="w-6 h-6 mr-2" />
            Start Game
          </Button>
        </div>
      </div>
    );
  }

  // Flashing Screen
  if (gameState === 'flashing') {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 flex flex-col items-center justify-center p-4">
        {!examMode && (
          <button
            onClick={handleExit}
            className="absolute top-4 left-4 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg backdrop-blur-lg border border-white/30 transition-all z-50"
          >
            ← Exit
          </button>
        )}
        <div className="w-full max-w-5xl">
          <div className="flex justify-between items-center mb-6 px-4">
            <div className="text-xl font-bold text-white/90 bg-white/10 px-6 py-3 rounded-xl backdrop-blur-lg">
              Round {currentRound + 1} / {config.numberOfRounds}
            </div>
            <div className="text-lg text-white/80 flex items-center bg-white/10 px-6 py-3 rounded-xl backdrop-blur-lg">
              <Eye className="w-5 h-5 mr-2 animate-pulse" />
              Watch Carefully
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl border-2 border-white/20 min-h-[400px] flex items-center justify-center mb-6">
            {isFlashing && displayDigit ? (
              <div className="text-[15rem] font-black text-white drop-shadow-2xl">
                {displayDigit}
              </div>
            ) : (
              <div className="text-8xl text-white/20">•</div>
            )}
          </div>

          <div className="flex justify-center gap-3">
            {Array.from({ length: parseInt(config.sequenceLength) }).map((_, idx) => (
              <div
                key={idx}
                className={`h-4 rounded-full transition-all duration-300 ${
                  idx < currentDigitIndex ? 'w-4 bg-green-400 shadow-lg shadow-green-400/50' :
                  idx === currentDigitIndex ? 'w-10 bg-white animate-pulse shadow-lg shadow-white/50' :
                  'w-4 bg-white/20'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Input Screen
  if (gameState === 'input') {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-blue-600 via-cyan-600 to-teal-600 flex flex-col items-center justify-center p-4">
        {!examMode && (
          <button
            onClick={handleExit}
            className="absolute top-4 left-4 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg backdrop-blur-lg border border-white/30 transition-all z-50"
          >
            ← Exit
          </button>
        )}
        <div className="w-full max-w-4xl">
          <div className="flex justify-between items-center mb-8 px-4">
            <div className="text-xl font-bold text-white bg-white/20 px-6 py-3 rounded-xl backdrop-blur-lg">
              Round {currentRound + 1} / {config.numberOfRounds}
            </div>
            <div className="text-lg text-white font-bold flex items-center bg-white/20 px-6 py-3 rounded-xl backdrop-blur-lg">
              Type the sequence!
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl border-2 border-white/30 p-10">
            <Label className="text-2xl text-white font-bold mb-6 block">Enter the number sequence:</Label>
            <Input
              type="text"
              value={userInput}
              onChange={(e) => setUserInput(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="Type here..."
              className="text-6xl text-center font-black py-8 bg-white/20 border-2 border-white/40 text-white placeholder:text-white/30 rounded-2xl"
              maxLength={parseInt(config.sequenceLength)}
              autoFocus
            />
            <p className="text-xl text-white/80 mt-4 font-semibold">
              {userInput.length} / {config.sequenceLength} digits
            </p>
          </div>

          <Button
            onClick={handleSubmit}
            disabled={userInput.length !== parseInt(config.sequenceLength)}
            size="lg"
            className="mt-6 w-full bg-white text-blue-600 hover:bg-white/90 disabled:bg-white/30 disabled:text-white/50 text-xl py-6 font-bold shadow-2xl rounded-2xl hover:scale-105 transition-all"
          >
            Submit Answer →
          </Button>
        </div>
      </div>
    );
  }

  // Result Screen
  if (gameState === 'result') {
    const correctCount = results.filter(r => r.correct).length;
    const accuracy = (correctCount / results.length) * 100;

    return (
      <div className="fixed inset-0 bg-gradient-to-br from-amber-500 via-orange-500 to-pink-600 flex items-center justify-center p-4 overflow-auto">
        {!examMode && (
          <button
            onClick={handleExit}
            className="absolute top-4 left-4 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg backdrop-blur-lg border border-white/30 transition-all z-50"
          >
            ← Exit
          </button>
        )}
        <div className="w-full max-w-5xl py-6">
          <div className="text-center space-y-6 animate-fade-in">
            <div className="w-24 h-24 bg-white/20 backdrop-blur-lg rounded-full flex items-center justify-center mx-auto shadow-2xl border-2 border-white/30">
              <Trophy className="w-14 h-14 text-white" />
            </div>

            <h2 className="text-5xl font-black text-white drop-shadow-2xl">Game Complete!</h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-lg p-6 rounded-2xl shadow-2xl border border-white/30">
                <p className="text-lg text-white/80 font-semibold mb-2">Score</p>
                <p className="text-5xl font-black text-white drop-shadow-lg">{correctCount}<span className="text-3xl">/{results.length}</span></p>
              </div>
              <div className="bg-white/10 backdrop-blur-lg p-6 rounded-2xl shadow-2xl border border-white/30">
                <p className="text-lg text-white/80 font-semibold mb-2">Accuracy</p>
                <p className="text-5xl font-black text-white drop-shadow-lg">{accuracy.toFixed(1)}<span className="text-3xl">%</span></p>
              </div>
              <div className="bg-white/10 backdrop-blur-lg p-6 rounded-2xl shadow-2xl border border-white/30">
                <p className="text-lg text-white/80 font-semibold mb-2">Time</p>
                <p className="text-4xl font-black text-white drop-shadow-lg flex items-center justify-center">
                  <Clock className="w-10 h-10 mr-2" />
                  {Math.floor((Date.now() - startTime) / 1000)}<span className="text-2xl">s</span>
                </p>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-lg p-6 rounded-2xl shadow-2xl border border-white/20 max-w-3xl mx-auto max-h-80 overflow-y-auto">
              <h3 className="font-bold text-2xl text-white mb-4">Round Summary</h3>
              <div className="space-y-2">
                {results.map((result, idx) => (
                  <div key={idx} className={`flex items-center justify-between p-4 rounded-xl backdrop-blur-sm ${
                    result.correct ? 'bg-green-400/20 border border-green-300/50' : 'bg-red-400/20 border border-red-300/50'
                  }`}>
                    <span className="text-base text-white font-bold">Round {result.round}</span>
                    <span className="font-mono text-lg text-white font-bold">
                      {result.sequence} → {result.userAnswer}
                    </span>
                    {result.correct ? (
                      <Check className="w-6 h-6 text-green-300 drop-shadow-lg" />
                    ) : (
                      <X className="w-6 h-6 text-red-300 drop-shadow-lg" />
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-4 justify-center">
              {!examMode && (
                <Button
                  onClick={resetGame}
                  size="lg"
                  className="bg-white text-orange-600 hover:bg-white/90 text-xl py-5 px-10 font-bold shadow-2xl rounded-2xl hover:scale-105 transition-all"
                >
                  🎮 Play Again
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

    return null;
  };

  // Main render: Wrap with fullscreen container
  return (
    <div ref={gameContainerRef}>
      {renderGameContent()}
    </div>
  );
};

export default MentalMemoryGame;
